import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  buildEventApplicantsListSnapshot,
  saveEventApplicantsListState,
} from "../../../../utils/companyPortal/eventApplicantsListState";
import { getCompanyEventRequestsRequest } from "../../../../store/CompanyPortal/EventRequests/actions";
import { getAdminEventRequestsRequest } from "../../../../store/AdminEventRequests/actions";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  isApplicantsActionsLocked,
  shouldShowCheckInSummary,
  shouldShowJoinedCheckInFilter,
} from "../../../../utils/companyPortal/eventEditRules";
import {
  canAddCompanyModule,
  canDeleteCompanyModule,
  canEditCompanyModule,
  canViewCompanyModules,
} from "../../../../utils/Permissions";
import { useAdminModulePermission } from "../../../../utils/adminPortalHelpers";
import { adminPermissionModules } from "../../../../utils/Constants";
import { UIButton } from "../../../ui";
import { joiningRequestStatuses } from "./constants";
import ApplicantsJoinedHeader from "./ApplicantsJoinedHeader";
import ApplicantsListPanel from "./ApplicantsListPanel";
import ApplicantsModals from "./modals/ApplicantsModals";
import useApplicantTags from "./hooks/company/useApplicantTags";
import { canManageTagRow, getTagEligibility } from "./tagEligibility";
import useApplicantsBulkModals from "./hooks/company/useApplicantsBulkModals";
import useAdminApplicantsBulkModals from "./hooks/admin/useAdminApplicantsBulkModals";
import useApplicantsExport from "./hooks/company/useApplicantsExport";
import useApplicantsList from "./hooks/company/useApplicantsList";
import useAdminApplicantsList from "./hooks/admin/useAdminApplicantsList";
import useApproveRequests from "./hooks/company/useApproveRequests";
import useChangeRole from "./hooks/company/useChangeRole";
import useAdminChangeRole from "./hooks/admin/useAdminChangeRole";
import useRejectRequests from "./hooks/company/useRejectRequests";
import useAdminRejectRequests from "./hooks/admin/useAdminRejectRequests";
import useReactivateRequests from "./hooks/company/useReactivateRequests";
import useEditRemovalReason from "./hooks/company/useEditRemovalReason";
import useAdminReactivateRequests from "./hooks/admin/useAdminReactivateRequests";
import useAdminEditRemovalReason from "./hooks/admin/useAdminEditRemovalReason";

import "./Applicants.scss";

const JoiningRequestsApplicants = ({
  event,
  onNavigateToZones,
  onRefreshEvent,
  portalVariant = "company-portal",
  enabledStatuses,
  initialStatus = joiningRequestStatuses.pending,
}) => {
  const isAdminPortal = portalVariant === "admin-portal";
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const myCompanyId = login?.company?.id;
  const joiningEventsModule = companyPermissionModules.joiningEvents;
  const joiningEventsPerm = useAdminModulePermission(
    adminPermissionModules.joiningEvents,
  );
  const canAdd = isAdminPortal
    ? joiningEventsPerm.canAdd
    : canAddCompanyModule(joiningEventsModule, login);
  const canEdit = isAdminPortal
    ? joiningEventsPerm.canEdit
    : canEditCompanyModule(joiningEventsModule, login);
  const canDelete = isAdminPortal
    ? joiningEventsPerm.canDelete
    : canDeleteCompanyModule(joiningEventsModule, login);
  // Export is a read operation gated on view (deliberate AC deviation — see
  // COMPANY_PERMISSIONS_IMPLEMENTATION_PLAN.md Phase 6).
  const canView = isAdminPortal
    ? joiningEventsPerm.canView
    : canViewCompanyModules(joiningEventsModule, login);
  const applicantsTab = messages[lang].events.viewEvents.applicantsTab;
  const sharedLabels = messages[lang].shared;
  const tableLabels = applicantsTab.joiningRequestsTable;
  const statusTabLabels = applicantsTab.joiningRequestsStatusTabs;
  const joiningRequestsCopy = applicantsTab.joiningRequests;
  const bulkActionsCopy = joiningRequestsCopy.bulkActions;
  const joinedSummaryCopy = applicantsTab.joinedApplicantsSummary;
  const allocateCopy = joiningRequestsCopy.allocateModal;
  const unassignCopy = joiningRequestsCopy.unassignModal;
  const removeCopy = joiningRequestsCopy.removeModal;
  const reactivateCopy = joiningRequestsCopy.reactivateModal;
  const editRemovalReasonCopy = joiningRequestsCopy.editRemovalReasonModal;
  const changeRoleCopy = joiningRequestsCopy.changeRoleModal;
  const companyApplicantsList = useApplicantsList({
    event,
    lang,
    sharedLabels,
    enabled: !isAdminPortal,
  });
  const adminApplicantsList = useAdminApplicantsList({
    event,
    lang,
    sharedLabels,
    initialStatus,
    enabledStatuses,
    enabled: isAdminPortal,
  });
  const applicantsList = isAdminPortal ? adminApplicantsList : companyApplicantsList;
  const {
    activeStatus,
    allocateZoneOptions,
    cityOptions,
    eventRoleOptions,
    eventShiftOptions,
    eventZoneOptions,
    filters,
    listParams,
    localizedRows,
    loyalApplicantsCount,
    meta,
    nationalityLoading,
    nationalityOptions,
    nationalitySearch,
    nonLoyalApplicantsCount,
    checkedInCount,
    notCheckedInCount,
    page,
    pageSize,
    ratingOptions,
    tagOptions,
    rows,
    search,
    selectedApplicants,
    selectedIds,
    setNationalitySearch,
    setSelectedIds,
    setSelectedRowsById,
    shiftFilterOptions,
    summary,
    handleFilterChange,
    handleNationalityOpen,
    handlePageSizeChange,
    handleSearchChange,
    handleSelectCurrentPage,
    handleSelectRow,
    handleStatusChange,
  } = applicantsList;

  const {
    approveApplicants,
    approveModalVariant,
    isApproveModalOpen,
    openApproveModal,
    closeApproveModal,
    confirmApprove,
  } = useApproveRequests({
    portalVariant,
    eventId: event?.id,
    rows: localizedRows,
    selectedIds,
    listParams,
    setSelectedIds,
  });

  const companyReject = useRejectRequests({
    rows: localizedRows,
    selectedIds,
    listParams,
    setSelectedIds,
  });
  const adminReject = useAdminRejectRequests({
    rows: localizedRows,
    selectedIds,
    listParams,
    setSelectedIds,
  });
  const {
    rejectApplicants,
    isRejectModalOpen,
    openRejectModal,
    closeRejectModal,
    confirmReject,
  } = isAdminPortal ? adminReject : companyReject;

  const companyReactivate = useReactivateRequests({
    rows: localizedRows,
    selectedIds,
    listParams,
    setSelectedIds,
    onRefreshEvent,
    reactivateCopy,
  });
  const adminReactivate = useAdminReactivateRequests({
    rows: localizedRows,
    selectedIds,
    listParams,
    setSelectedIds,
    onRefreshEvent,
    reactivateCopy,
  });
  const {
    reactivateApplicants,
    isReactivateModalOpen,
    openReactivateModal,
    closeReactivateModal,
    confirmReactivate,
  } = isAdminPortal ? adminReactivate : companyReactivate;

  const companyEditRemovalReason = useEditRemovalReason({
    rows: localizedRows,
    selectedIds,
    listParams,
    setSelectedIds,
    onRefreshEvent,
  });
  const adminEditRemovalReason = useAdminEditRemovalReason({
    rows: localizedRows,
    selectedIds,
    listParams,
    setSelectedIds,
    onRefreshEvent,
  });
  const {
    editReasonApplicants,
    isEditReasonModalOpen,
    openEditReasonModal,
    closeEditReasonModal,
    confirmEditReason,
  } = isAdminPortal ? adminEditRemovalReason : companyEditRemovalReason;

  const refreshApplicants = (params) => {
    dispatch(
      isAdminPortal
        ? getAdminEventRequestsRequest(params)
        : getCompanyEventRequestsRequest(params),
    );
  };
  const {
    selectedApplicant,
    isBulkTag,
    deleteTagTargetIds,
    openApplicantTag,
    openBulkTag,
    saveTag,
    openDeleteBulkTag,
    closeApplicantTag,
    closeDeleteTag,
    confirmDeleteTag,
  } = useApplicantTags({
    rows,
    selectedIds,
    rowsForBulkTag: localizedRows,
    portalVariant,
    myCompanyId,
    listParams,
    refreshList: refreshApplicants,
    setSelectedIds,
    setSelectedRowsById,
  });
  const tagEligibility = getTagEligibility(selectedApplicants, {
    portalVariant,
    myCompanyId,
    mode: "assign",
  });
  const removeTagEligibility = getTagEligibility(selectedApplicants, {
    portalVariant,
    myCompanyId,
    mode: "remove",
  });
  // Edit is disabled only when there's no selection or every selected tag is
  // owned by another company; the all-own-tagged case opens the modal (which
  // shows the "remove existing tags first" message).
  const isEditTagDisabled =
    tagEligibility.total === 0 ||
    tagEligibility.otherOwnerCount === tagEligibility.total;
  const isDeleteTagDisabled =
    removeTagEligibility.eligibleUserIds.length === 0 &&
    removeTagEligibility.otherOwnerCount > 0;
  const canManageRowTag = (row) =>
    canManageTagRow(row, { portalVariant, myCompanyId });

  const applicantsExport = useApplicantsExport({
    event,
    lang,
    listParams,
    selectedIds,
    portalVariant,
  });
  const companyApplicantsBulkModals = useApplicantsBulkModals({
    listParams,
    onRefreshEvent,
    selectedApplicants,
    setSelectedIds,
    setSelectedRowsById,
  });
  const adminApplicantsBulkModals = useAdminApplicantsBulkModals({
    listParams,
    onRefreshEvent,
    selectedApplicants,
    setSelectedIds,
    setSelectedRowsById,
  });
  const applicantsBulkModals = isAdminPortal
    ? adminApplicantsBulkModals
    : companyApplicantsBulkModals;
  const companyChangeRole = useChangeRole({
    listParams,
    selectedApplicants,
    setSelectedIds,
    setSelectedRowsById,
  });
  const adminChangeRole = useAdminChangeRole({
    listParams,
    selectedApplicants,
    setSelectedIds,
    setSelectedRowsById,
  });
  const changeRole = isAdminPortal ? adminChangeRole : companyChangeRole;

  const total = meta.count || 0;
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);
  const pages = meta.pages || Math.ceil(total / pageSize);
  const selectedLabel = joiningRequestsCopy.selectedCount.replace(
    "{count}",
    selectedIds.length,
  );
  const isJoinedStatus = activeStatus === joiningRequestStatuses.joined;
  const isPendingStatus = activeStatus === joiningRequestStatuses.pending;
  const showCheckInSummary = shouldShowCheckInSummary(event?.status);
  const applicantsActionsLocked = isApplicantsActionsLocked(event);
  const renderJoinedExportButton = isJoinedStatus && canView && (
    <UIButton
      variant="primary"
      size="large"
      appearance="outline"
      loading={applicantsExport.isExporting && applicantsExport.exportMode === "all"}
      onClick={() => applicantsExport.openModal("all")}
      className="event-applicants__export-all"
    >
      {joinedSummaryCopy.exportAll}
    </UIButton>
  );

  const resolveApplicantUserId = (row) => {
    if (!row) return null;
    return row.userId ?? row.raw?.user_id ?? row.raw?.user?.id ?? null;
  };

  const navigateToApplicantProfile = (row) => {
    const userId = resolveApplicantUserId(row);
    if (!userId || !event?.id) return;

    const listPortal = isAdminPortal ? "admin" : "company";

    saveEventApplicantsListState(
      event.id,
      buildEventApplicantsListSnapshot({
        activeStatus,
        search,
        filters,
        page,
        pageSize,
      }),
      listPortal,
    );

    const fromEventState = {
      fromEvent: {
        eventId: event.id,
        eventName: event.name,
        eventStatus: event.status,
      },
    };

    if (isAdminPortal) {
      history.push(
        ROUTE_PATHS.viewApplicant.replace(":id", userId),
        fromEventState,
      );
      return;
    }

    history.push(
      ROUTE_PATHS.companyViewApplicantProfile.replace(":id", userId),
      fromEventState,
    );
  };

  return (
    <Box className="event-applicants">
      <ApplicantsJoinedHeader
        labels={statusTabLabels}
        summary={summary}
        value={activeStatus}
        isJoined={isJoinedStatus}
        isPending={isPendingStatus}
        checkedInCount={checkedInCount}
        notCheckedInCount={notCheckedInCount}
        loyalApplicantsCount={loyalApplicantsCount}
        nonLoyalApplicantsCount={nonLoyalApplicantsCount}
        copy={joinedSummaryCopy}
        enabledStatuses={enabledStatuses}
        showCheckInSummary={showCheckInSummary}
        showPendingApplicantTypeSummary={!isAdminPortal}
        onChange={handleStatusChange}
      />
      <ApplicantsListPanel
        activeStatus={activeStatus}
        applicantsTab={applicantsTab}
        bulkActionsCopy={bulkActionsCopy}
        canAdd={canAdd}
        canEdit={canEdit}
        canDelete={canDelete}
        canView={canView}
        filters={filters}
        joiningRequestsCopy={joiningRequestsCopy}
        localizedRows={localizedRows}
        nationalityLoading={nationalityLoading}
        nationalityOptions={nationalityOptions}
        nationalitySearch={nationalitySearch}
        page={page}
        pageSize={pageSize}
        pages={pages}
        rangeFrom={rangeFrom}
        rangeTo={rangeTo}
        ratingOptions={ratingOptions}
        cityOptions={cityOptions}
        renderJoinedExportButton={renderJoinedExportButton}
        roleOptions={eventRoleOptions}
        rows={rows}
        search={search}
        selectedIds={selectedIds}
        selectedLabel={selectedLabel}
        shiftFilterOptions={shiftFilterOptions}
        tableLabels={tableLabels}
        total={total}
        zoneOptions={eventZoneOptions}
        onAddEditTag={openBulkTag}
        onAllocate={applicantsBulkModals.openAllocateModal}
        onApprove={openApproveModal}
        onDeleteTag={openDeleteBulkTag}
        isEditTagDisabled={isEditTagDisabled}
        isDeleteTagDisabled={isDeleteTagDisabled}
        canManageRowTag={canManageRowTag}
        onEditRole={changeRole.openBulkChangeRoleModal}
        onExport={() => applicantsExport.openModal("selected")}
        onFilterChange={handleFilterChange}
        onNationalityOpen={handleNationalityOpen}
        onNationalitySearchChange={setNationalitySearch}
        onTagClick={openApplicantTag}
        onPageChange={applicantsList.setPage}
        onPageSizeChange={handlePageSizeChange}
        onReject={openRejectModal}
        onRemove={applicantsBulkModals.openRemoveModal}
        onSearchChange={handleSearchChange}
        onSelectAll={handleSelectCurrentPage}
        onSelectRow={handleSelectRow}
        onRowClick={navigateToApplicantProfile}
        onUnassign={applicantsBulkModals.openUnassignModal}
        onReactivate={openReactivateModal}
        onEditReason={openEditReasonModal}
        showApplicantTypeFilter={!isAdminPortal}
        showCheckInFilter={shouldShowJoinedCheckInFilter(event?.status)}
        applicantsActionsLocked={applicantsActionsLocked}
        statusTabTotal={summary[activeStatus] ?? 0}
      />
      <ApplicantsModals
        applicantsExport={applicantsExport}
        applicantsTab={applicantsTab}
        approveApplicants={approveApplicants}
        approveModalVariant={approveModalVariant}
        allocateCopy={allocateCopy}
        allocateZoneOptions={allocateZoneOptions}
        closeAllocateModal={applicantsBulkModals.closeAllocateModal}
        closeApproveModal={closeApproveModal}
        closeChangeRoleModal={changeRole.closeChangeRoleModal}
        closeApplicantTag={closeApplicantTag}
        closeDeleteTag={closeDeleteTag}
        closeRejectModal={closeRejectModal}
        closeRemoveModal={applicantsBulkModals.closeRemoveModal}
        closeReactivateModal={closeReactivateModal}
        closeEditReasonModal={closeEditReasonModal}
        closeUnassignModal={applicantsBulkModals.closeUnassignModal}
        confirmApprove={confirmApprove}
        confirmChangeRole={changeRole.confirmChangeRole}
        confirmDeleteTag={confirmDeleteTag}
        confirmReject={confirmReject}
        confirmReactivate={confirmReactivate}
        confirmEditReason={confirmEditReason}
        deleteTagTargetIds={deleteTagTargetIds}
        eventRoleOptions={eventRoleOptions}
        eventShiftOptions={eventShiftOptions}
        isAllocateModalOpen={applicantsBulkModals.isAllocateModalOpen}
        isApproveModalOpen={isApproveModalOpen}
        isChangeRoleModalOpen={changeRole.isChangeRoleModalOpen}
        isRejectModalOpen={isRejectModalOpen}
        isRemoveModalOpen={applicantsBulkModals.isRemoveModalOpen}
        isReactivateModalOpen={isReactivateModalOpen}
        isEditReasonModalOpen={isEditReasonModalOpen}
        isUnassignModalOpen={applicantsBulkModals.isUnassignModalOpen}
        isBulkTag={isBulkTag}
        tagOptions={tagOptions}
        portalVariant={portalVariant}
        myCompanyId={myCompanyId}
        onAllocateConfirm={applicantsBulkModals.handleAllocateConfirm}
        onNavigateToZones={onNavigateToZones}
        onRemoveConfirm={applicantsBulkModals.handleRemoveConfirm}
        onSaveTag={saveTag}
        onUnassignConfirm={applicantsBulkModals.handleUnassignConfirm}
        rejectApplicants={rejectApplicants}
        reactivateApplicants={reactivateApplicants}
        editReasonApplicants={editReasonApplicants}
        reactivateCopy={reactivateCopy}
        editRemovalReasonCopy={editRemovalReasonCopy}
        changeRoleApplicants={changeRole.changeRoleApplicants}
        changeRoleCopy={changeRoleCopy}
        removeCopy={removeCopy}
        selectedApplicant={selectedApplicant}
        selectedApplicants={selectedApplicants}
        sharedLabels={sharedLabels}
        unassignCopy={unassignCopy}
      />
    </Box>
  );
};

JoiningRequestsApplicants.propTypes = {
  event: PropTypes.object,
  onNavigateToZones: PropTypes.func,
  onRefreshEvent: PropTypes.func,
  portalVariant: PropTypes.oneOf(["company-portal", "admin-portal"]),
  enabledStatuses: PropTypes.arrayOf(PropTypes.string),
  initialStatus: PropTypes.string,
};

JoiningRequestsApplicants.defaultProps = {
  event: null,
  onNavigateToZones: undefined,
  onRefreshEvent: undefined,
  portalVariant: "company-portal",
  enabledStatuses: undefined,
  initialStatus: joiningRequestStatuses.pending,
};

export default JoiningRequestsApplicants;
